import "mapbox-gl/dist/mapbox-gl.css";
import "./DashboardMap.css";

import { Link } from "react-router-dom";

import {
  ClearDarkIcon,
  FilterControlsDarkIcon,
  LegendDarkIcon,
  MaximizeDarkIcon,
  MinimizeDarkIcon,
  MinusDarkIcon,
  PlusDarkIcon,
  SearchOutlineDarkIcon,
} from "../../../assets";
import { ErrorBox, LoadingSpinner, TextInput } from "../..";
import { DashboardMapPin, DashboardMapProps } from "./DashboardMapProps";
import { useDashboardMap } from "./useDashboardMap";

export const DashboardMap = (props: DashboardMapProps): JSX.Element => {
  const {
    product = "seed",
    width = "100%",
    colourBy = "type",
    statusType = "registry-status",
    showSearch = true,
    showFilters = true,
    showZoomControls = true,
    showKey = true,
    isExpandable = false,
    onExpandToggle,
    isLoading = false,
    emptyMessage = "No projects match your search",
  } = props;

  const {
    mapContainerRef,
    hasError,
    filterOptions,
    selectedFilters,
    filterDimension,
    isFilterOpen,
    filterRef,
    keyRef,
    cardRef,
    keyEntries,
    isKeyOpen,
    selectedPin,
    overlayPoint,
    isEmpty,
    isExpanded,
    containerHeight,
    canvasHeight,
    searchText,
    searchRef,
    searchResults,
    isSearchListOpen,
    pinColourFor,
    onSearchChange,
    onClearSearch,
    onSelectSearchResult,
    onToggleFilterOpen,
    onToggleFilter,
    onClearFilters,
    onToggleKeyOpen,
    onToggleExpand,
    onContainerTransitionEnd,
    onZoomIn,
    onZoomOut,
  } = useDashboardMap(props);

  const filterLabel = filterDimension === "phase" ? "phase" : "type";

  const showExpandButton = isExpandable || !!onExpandToggle;
  const showKeyButton = showKey && keyEntries.length > 0;

  /* The coloured chip reflects the active colour dimension; the meta line shows the other attribute */
  const resolveMetaLabel = (pin: DashboardMapPin): string | undefined => {
    if (colourBy === "phase") return pin.category;
    return statusType === "phase" ? pin.phase : pin.registryStatus;
  };
  const overlayChipLabel = selectedPin && (colourBy === "phase" ? selectedPin.phase : selectedPin.category);
  const overlayMetaLabel = selectedPin ? resolveMetaLabel(selectedPin) : undefined;
  const overlayMetaHasDot = colourBy === "type" && statusType === "registry-status";

  if (hasError) {
    return (
      <div className={`DashboardMap DashboardMap_${product}`} style={{ width, height: containerHeight }}>
        <ErrorBox title="Map unavailable" text="The map could not be loaded. Please try again later." />
      </div>
    );
  }

  return (
    <div
      className={`DashboardMap DashboardMap_${product} ${isExpanded ? "DashboardMap_expanded" : ""}`}
      style={{ width, height: containerHeight }}
      onTransitionEnd={onContainerTransitionEnd}
    >
      <div ref={mapContainerRef} className="DashboardMap_canvas" style={{ height: canvasHeight }} />

      {(showSearch || showFilters || showKeyButton || showExpandButton) && (
        <div className="DashboardMap_controls">
          {showSearch && (
            <div className="DashboardMap_search" ref={searchRef}>
              <TextInput
                name="dashboard-map-search"
                placeholder="Search"
                value={searchText}
                icon={
                  searchText ? (
                    <button
                      type="button"
                      className="DashboardMap_searchClear"
                      aria-label="Clear search"
                      onClick={onClearSearch}
                    >
                      <ClearDarkIcon />
                    </button>
                  ) : (
                    <SearchOutlineDarkIcon />
                  )
                }
                debounceMilliseconds={300}
                onChange={onSearchChange}
              />
              {isSearchListOpen && (
                <div className="DashboardMap_searchResults">
                  {searchResults.length ? (
                    searchResults.map((pin) => (
                      <button
                        key={pin.id}
                        type="button"
                        className="DashboardMap_searchResult"
                        onClick={() => onSelectSearchResult(pin)}
                      >
                        <span className="DashboardMap_searchResultName">{pin.name}</span>
                        {pin.location && <span className="DashboardMap_searchResultLocation">{pin.location}</span>}
                      </button>
                    ))
                  ) : (
                    <p className="DashboardMap_searchNoResults">No projects match your search</p>
                  )}
                </div>
              )}
            </div>
          )}
          {showFilters && !!filterOptions.length && (
            <div className="DashboardMap_filter" ref={filterRef}>
              <button
                type="button"
                className="DashboardMap_iconButton"
                aria-label={`Filter by ${filterLabel}`}
                aria-expanded={isFilterOpen}
                onClick={onToggleFilterOpen}
              >
                <FilterControlsDarkIcon />
              </button>
              {isFilterOpen && (
                <div className="DashboardMap_filterPopover">
                  <div className="DashboardMap_filterPopoverHeader">
                    <p className="body2">Filter by {filterLabel}</p>
                    {!!selectedFilters.length && (
                      <button type="button" className="body2 DashboardMap_clearFilters" onClick={onClearFilters}>
                        Clear
                      </button>
                    )}
                  </div>
                  <div role="group" aria-label={`Filter by ${filterLabel}`}>
                    {filterOptions.map((option) => {
                      const isSelected = selectedFilters.includes(option);

                      return (
                        <button
                          key={option}
                          type="button"
                          role="checkbox"
                          aria-checked={isSelected}
                          className={`DashboardMap_filterOption ${isSelected ? "DashboardMap_filterOption_selected" : ""}`}
                          onClick={() => onToggleFilter(option)}
                        >
                          <span className="DashboardMap_filterCheckbox" aria-hidden="true" />
                          <span className="DashboardMap_filterOptionLabel">{option}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          {showKeyButton && (
            <div className="DashboardMap_key" ref={keyRef}>
              <button
                type="button"
                className="DashboardMap_iconButton"
                aria-label="Colour key"
                aria-expanded={isKeyOpen}
                onClick={onToggleKeyOpen}
              >
                <LegendDarkIcon />
              </button>
              {isKeyOpen && (
                <div className="DashboardMap_keyPopover">
                  <p className="body2 DashboardMap_keyHeader">
                    {colourBy === "phase" ? "Project phase" : "Project type"}
                  </p>
                  {keyEntries.map((entry) => (
                    <div key={entry.label} className="DashboardMap_keyRow">
                      <span
                        className={`DashboardMap_keySwatch DashboardMap_keySwatch_${entry.colour}`}
                        aria-hidden="true"
                      />
                      <span className="DashboardMap_keyLabel">{entry.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {showExpandButton && (
            <button
              type="button"
              className="DashboardMap_iconButton"
              aria-label={isExpanded ? "Collapse map" : "Expand map"}
              onClick={onToggleExpand}
            >
              {isExpanded ? <MinimizeDarkIcon /> : <MaximizeDarkIcon />}
            </button>
          )}
        </div>
      )}

      {showZoomControls && (
        <div className="DashboardMap_zoomControls">
          <button type="button" className="DashboardMap_iconButton" aria-label="Zoom in" onClick={onZoomIn}>
            <PlusDarkIcon />
          </button>
          <button type="button" className="DashboardMap_iconButton" aria-label="Zoom out" onClick={onZoomOut}>
            <MinusDarkIcon />
          </button>
        </div>
      )}

      {selectedPin && overlayPoint && (
        <div
          ref={cardRef}
          className={`DashboardMap_overlayCard ${overlayPoint.y < 260 ? "DashboardMap_overlayCard_below" : ""} ${
            selectedPin.link ? "DashboardMap_overlayCard_linked" : ""
          }`}
          style={{ left: overlayPoint.x, top: overlayPoint.y }}
        >
          {selectedPin.link ? (
            <Link className="DashboardMap_overlayTitle DashboardMap_overlayTitleLink" to={selectedPin.link}>
              {selectedPin.name}
            </Link>
          ) : (
            <p className="DashboardMap_overlayTitle">{selectedPin.name}</p>
          )}
          {selectedPin.location && <p className="DashboardMap_overlayLocation">{selectedPin.location}</p>}
          {overlayChipLabel && (
            <div className="DashboardMap_overlayTags">
              <span className={`DashboardMap_overlayTag DashboardMap_overlayTag_${pinColourFor(selectedPin)}`}>
                {overlayChipLabel}
              </span>
            </div>
          )}
          {overlayMetaLabel && (
            <p className="DashboardMap_overlayStatus">
              {overlayMetaHasDot && <span className="DashboardMap_overlayStatusDot" aria-hidden="true" />}
              {overlayMetaLabel}
            </p>
          )}
        </div>
      )}

      {!isLoading && isEmpty && (
        <div className="DashboardMap_emptyState">
          <p className="body1">{emptyMessage}</p>
        </div>
      )}

      {isLoading && (
        <div className="DashboardMap_loadingOverlay">
          <LoadingSpinner type="dark" />
        </div>
      )}
    </div>
  );
};
